import { isStaffUser } from 'utils/staff'
import { useCallback, useContext, useEffect, useMemo, useState } from 'react'
import { useHistory, useLocation } from 'react-router-dom'
import { CoreContext } from 'context/CoreContext'
import { useAuthLayout } from 'context/AuthLayoutContext'
import { ReadSearch } from 'services/search'
import { trackEvent } from 'services/analytics'
import { buildS3DownloadUrl } from 'services/s3Download'
import { apiRequest, exposeStrapiError } from 'utils'
import { languages } from 'utils/options'
import { buildSupportVideoRoute } from 'utils/supportVideos'
import useI18n from 'hooks/useI18n'

export const SEARCH_TYPES = ['all', 'videos', 'images', 'documents', 'learning', 'articles']
export function readSearchState(search) {
  const params = new URLSearchParams(search)
  const page = Number(params.get('page'))
  return {
    q: (params.get('q') || '').trim(),
    type: SEARCH_TYPES.includes(params.get('type')) ? params.get('type') : 'all',
    page: Number.isSafeInteger(page) && page > 0 ? Math.min(page, 10000) : 1,
    includeRelated: params.get('related') === 'true',
  }
}
export default function useController() {
  const location = useLocation()
  const history = useHistory()
  const { t } = useI18n()
  const { language, setSearchExpression, setModal, user } = useContext(CoreContext)
  const { setLayoutProps } = useAuthLayout()
  const { q, type, page, includeRelated } = useMemo(() => readSearchState(location.search), [location.search])
  const locale = useMemo(() => languages.find(item => item.id === language)?.ref || 'en', [language])
  const [data, setData] = useState(null)
  const [facetState, setFacetState] = useState({ query: '', values: {} })
  const [loading, setLoading] = useState(true)
  const [sharing, setSharing] = useState(false)
  const canShare = useMemo(() => isStaffUser(user), [user])
  const [error, setError] = useState(false)
  const [attempt, setAttempt] = useState(0)

  useEffect(() => { setLayoutProps({ filtred: true }); return () => setLayoutProps({}) }, [setLayoutProps])
  useEffect(() => { setSearchExpression(q) }, [q, setSearchExpression])
  useEffect(() => {
    const abort = new AbortController()
    let active = true
    setData(null)
    setSharing(false)
    setError(false)
    setLoading(q.length >= 2)
    if (q.length < 2) return () => abort.abort()
    // Header submits explicitly. Coalesce rapid URL/filter changes and cancel superseded requests.
    const timer = setTimeout(async () => {
      try {
        const response = await apiRequest(() => ReadSearch(q, { version: 2, type, page, pageSize: 20, locale, includeRelated, signal: abort.signal }))
        if (!active) return
        if (exposeStrapiError(response) || response?.version !== 2) { setError(true); return }
        setData(response)
        setFacetState({ query: q, values: response.facets })
        trackEvent(response.total ? 'SEARCH' : 'SEARCH_NO_RESULTS', {
          search_term: q, results_count: response.total, had_results: response.total > 0,
          search_page: page, locale, result_type: type, filter_type: 'content_type', filter_value: type,
        })
      } catch (failure) {
        if (active && failure?.name !== 'AbortError') setError(true)
      } finally { if (active) setLoading(false) }
    }, 200)
    return () => { active = false; clearTimeout(timer); abort.abort() }
  }, [q, type, page, locale, includeRelated, attempt])

  const updateUrl = useCallback((patch) => {
    const params = new URLSearchParams(location.search)
    Object.entries(patch).forEach(([key, value]) => value === null ? params.delete(key) : params.set(key, String(value)))
    history.push({ pathname: '/search', search: params.toString() })
  }, [history, location.search])
  const onTypeChange = useCallback((next) => updateUrl({ type: next === 'all' ? null : next, page: null }), [updateUrl])
  const onPageChange = useCallback((_, next) => { updateUrl({ page: next + 1 }); window.scrollTo({ top: 0, behavior: 'smooth' }) }, [updateUrl])
  const showRelated = useCallback(() => updateUrl({ related: true, page: null }), [updateUrl])
  const retry = useCallback(() => setAttempt(value => value + 1), [])
  const onOpen = useCallback((item, index) => {
    trackEvent('SEARCH_RESULT_CLICK', {
      search_term: q, content_type: item.type, content_id: item.id,
      result_type: item.kind, result_position: (page - 1) * 20 + index + 1, results_count: data?.total || 0,
    })
    if (item.type === 'service-supports' && item.kind === 'videos' && item.categoryId) {
      history.push(buildSupportVideoRoute({ categoryId: item.categoryId, contentId: item.documentId || item.id }))
    } else if (['courses', 'partners-and-staffs', 'articles'].includes(item.type)) {
      history.push(`/mode/${item.type}/${item.id}`)
    } else if (item.document || (item.kind === 'images' && item.image?.url)) {
      const preview = item.mime === 'application/pdf' || ['images', 'videos'].includes(item.kind)
      const url = buildS3DownloadUrl({ sourceUrl: item.document || item.image.url, filename: item.title, disposition: preview ? 'inline' : 'attachment' })
      if (item.mime === 'application/pdf') setModal({ type: 'pdf-preview', title: item.title, url })
      else if (['images', 'videos'].includes(item.kind)) setModal({ type: item.kind === 'videos' ? 'video-preview' : 'image-preview', title: item.title, url, downloadUrl: buildS3DownloadUrl({ sourceUrl: item.document || item.image.url, filename: item.title, disposition: 'attachment' }) })
      else window.location.href = url
    }
  }, [data?.total, history, page, q, setModal])
  const tabs = useMemo(() => SEARCH_TYPES.filter(key => key === 'all' || key === type || (facetState.query === q && facetState.values?.[key] > 0)), [facetState, q, type])
  return { t, q, type, data, loading, error, tabs, sharing, setSharing, canShare, retry, onTypeChange, onPageChange, showRelated, onOpen }
}
